// File: src/pages/Invoices/InvoiceForm.jsx
import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Autocomplete, TextField, Dialog, DialogContent, DialogTitle } from "@mui/material";
import axios from "../../axios";
import invoiceApi from "../../api/invoiceApi";
import { useAuth } from "../../context/AuthContext";
import { PERMISSIONS } from "../../constants/permissions";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { Card, CardContent } from "../../components/ui/card";
import { SettingsContext } from "../../contexts/SettingsContext";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import CustomerForm from "../customerSuplier/CustomerForm";
import ImportInvoiceItemsDialog from "./ImportInvoiceItemsDialog";
import * as XLSX from "xlsx";

const initialFormState = {
  invoiceTypeId: 0,
  invoiceNumber: "",
  paidAmount: 0,
  issueDate: new Date().toISOString(),
  referenceDate: new Date().toISOString(),
  referenceNumber: "-1",
  customerId: 0,
  supplierId: 0,
  customerPrevBalance: 0,
  stockId: 0,
  toStockId: 0,
  salesmanId: null,
  paymentMethodId: 0,
  safeId: 0,
  bankId: 0,
  notes: "",
  discount: 0,
  invoiceStatus: 0,
  lines: [],
  journalEntryGlobalId: null, // ✅ Add this
};

export default function InvoiceForm({ viewMode = false }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { id, typeId } = useParams();
  const [searchParams] = useSearchParams();
  const returnOf = searchParams.get("returnOf");
  const fromQuoteId = searchParams.get("fromQuote") || searchParams.get("Qtation");

  const [editingId, setEditingId] = useState(id ?? null);
  const mode = viewMode
    ? "view"
    : editingId
    ? "edit"
    : returnOf
    ? "return"
    : fromQuoteId
    ? "fromQuote"
    : "new";

  const initialType = returnOf ? 3 : fromQuoteId ? 1 : typeId ? parseInt(typeId, 10) : 0;

  const companyId = parseInt(localStorage.getItem("companyId"), 10);
  const branchId = parseInt(localStorage.getItem("branchId"), 10);
  const userId = parseInt(localStorage.getItem("userId"), 10);
  
  // Security Context
  const restrictedSafeId = localStorage.getItem("safeId") ? parseInt(localStorage.getItem("safeId"), 10) : null;
  const restrictedStockId = localStorage.getItem("stockId") ? parseInt(localStorage.getItem("stockId"), 10) : null;
  
  // Multi-context support
  const allowedSafeIds = localStorage.getItem("allowedSafeIds") ? JSON.parse(localStorage.getItem("allowedSafeIds")) : [];
  const allowedStockIds = localStorage.getItem("allowedStockIds") ? JSON.parse(localStorage.getItem("allowedStockIds")) : [];

  const [layout, setLayout] = useState({ header: "", items: "" });

  const getLabel = (fieldName) => {
    return t(`invoice.fields.${fieldName}`);
  };

  const [visibleFields, setVisibleFields] = useState({
    InvoiceNumber: true,
    IssueDate: true,
    CustomerId: true,
    SupplierId: false,
    Notes: true,
    ProductId: true,
    Description: true,
    Quantity: true,
    UnitPrice: true,
    Discount: true,
    TaxPercent: true,
    LineTotal: true,
  });

  const fieldLabels = {
    InvoiceNumber: t('invoice.fields.invoiceNumber'),
    IssueDate: t('invoice.fields.invoiceDate'),
    CustomerId: t('invoice.fields.customer'),
    SupplierId: t('invoice.fields.supplier'),
    Notes: t('invoice.fields.notes'),
    ReferenceNumber: t('invoice.fields.referenceNumber'),
    ReferenceDate: t('invoice.fields.referenceDate'),
    ProductId: t('invoice.fields.item'),
    Description: t('invoice.fields.description'),
    Unit: t('invoice.fields.unit') || 'الوحدة',
    Quantity: t('invoice.fields.quantity'),
    UnitPrice: t('invoice.fields.price'),
    Discount: t('invoice.fields.discount'),
    TaxPercent: t('invoice.fields.taxPercent'),
    LineTotal: t('invoice.fields.total'),
  };

  const [visibleItemFields, setVisibleItemFields] = useState({
    ProductId: true,
    Description: true,
    Unit: true,
    Quantity: true,
    UnitPrice: true,
    Discount: true,
    TaxPercent: true,
    LineTotal: true,
    Net: true,
  });

  const focusLineField = (rowIndex, field) => {
    if (rowIndex == null || !field) return false;
    const element = document.querySelector(`[data-line-field="${field}-${rowIndex}"]`);
    if (!element || element.disabled) return false;
    element.focus();
    if (typeof element.select === "function") {
      element.select();
    }
    return true;
  };

  const getNextLineField = (field) => {
    const order = [
      { key: "qty", enabled: visibleItemFields.Quantity },
      { key: "price", enabled: visibleItemFields.UnitPrice },
      { key: "discount", enabled: visibleItemFields.Discount },
    ];
    const currentIndex = order.findIndex(x => x.key === field);
    if (currentIndex === -1) return null;
    const next = order.slice(currentIndex + 1).find(x => x.enabled);
    return next?.key ?? null;
  };

  const getFirstLineField = () => {
    if (visibleItemFields.ProductId) return "productName";
    if (visibleItemFields.Description) return "description";
    if (visibleItemFields.Unit) return "unitId";
    if (visibleItemFields.Quantity) return "qty";
    if (visibleItemFields.UnitPrice) return "price";
    if (visibleItemFields.Discount) return "discount";
    return null;
  };

  const { decimalPlaces } = useContext(SettingsContext);
  const stepValue = 1 / 10 ** decimalPlaces;

  const [types, setTypes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [methods, setMethods] = useState([]);
  const [safes, setSafes] = useState([]);
  const [banks, setBanks] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [products, setProducts] = useState([]);
  const [creditMethodId, setCreditMethodId] = useState(null);

  const [priceIncludesVAT, setPriceIncludesVAT] = useState(false);

  const [settings, setSettings] = useState({
    isPriceIncludesVAT: false,
    autoApproveInvoices: false,
    autoPrintAfterSave: false,
    allowNegativeSales: false,
    pricingPolicy: "",
    decimalPlaces: 2,
  });

  const [form, setForm] = useState({
    ...initialFormState,
    invoiceTypeId: initialType,
  });

  const [productSearch, setProductSearch] = useState("");
  const [showApproveButton, setShowApproveButton] = useState(false);
  const [isApproved, setIsApproved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [canUnapprove, setCanUnapprove] = useState(false);
  const [isZatcaLocked, setIsZatcaLocked] = useState(false);
  const [zatcaStatus, setZatcaStatus] = useState(null); // REPORTED, FAILED, CLEARED
  const [zatcaResponse, setZatcaResponse] = useState(null); // For details if needed
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth <= 768;
  });
  
  const [openCustomerModal, setOpenCustomerModal] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleImportSuccess = (importedItems) => {
    const newLines = importedItems.map(item => {
        const { product: p, qty, price } = item;
        const unitsList = Array.isArray(p.units) ? p.units : [];
        const bu = unitsList.find(u => u.isBaseUnit) || {};
        
        // Use imported price if available, otherwise calculate based on policy
        // Note: pickUnitPriceForDirection uses current form state (isInbound)
        const unitPrice = price !== null ? price : pickUnitPriceForDirection(bu, p);
        
        return {
            productId: p.id,
            productName: p?.arabicName ?? p?.nameAr ?? p?.name ?? "",
            description: p?.description ?? "",
            unitId: bu?.unitId || 0,
            unitName: bu?.unitNameAr || "",
            qty: parseFloat(qty).toFixed(decimalPlaces),
            price: parseFloat(unitPrice).toFixed(decimalPlaces),
            discount: parseFloat(0).toFixed(decimalPlaces),
            taxRate: parseFloat(p?.taxRate || 0).toFixed(decimalPlaces),
            unitsList,
        };
    });

    setForm(f => ({ ...f, lines: [...f.lines, ...newLines] }));
  };

  const handleCustomerSuccess = (newCustomer) => {
    setCustomers(prev => [...prev, newCustomer]);
    setForm(f => ({ ...f, customerId: newCustomer.id }));
    setOpenCustomerModal(false);
    toast.success("تم إضافة العميل بنجاح وتحديده");
  };
const togglePriceIncludesVAT = () => {
  setPriceIncludesVAT(prev => !prev);
};

  // إدراج متعدد للأصناف
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkSearch, setBulkSearch] = useState("");
  // خريطة: productId -> كمية
  const [bulkSelected, setBulkSelected] = useState({});

  // قراءة الباركود وإدراج الصنف تلقائياً
  const [barcode, setBarcode] = useState("");
  const [barcodeLoading, setBarcodeLoading] = useState(false);
  const pendingLineFocusRef = useRef(null);

  async function insertProductByBarcode(code) {
    const raw = (code || "").trim();
    if (!raw) return;
    try {
      setBarcodeLoading(true);
      // ابحث أولاً على مستوى المنتج
      let p = products.find(pr => (pr.barcode || "").toString() === raw);
      let chosenUnit = null;
      if (!p) {
        // ابحث على مستوى الوحدات لكل منتج
        for (const pr of products) {
          const units = Array.isArray(pr.units) ? pr.units : [];
          const match = units.find(u => (u.barcode || "").toString() === raw);
          if (match) {
            p = pr;
            chosenUnit = match;
            break;
          }
        }
      }

      if (!p) {
        toast.error("❌ لم يتم العثور على صنف بهذا الباركود");
        return;
      }

      // التحقق من الرصيد عند المبيعات الصادرة
      if (isOutbound) {
        try {
          const response = await axios.get(
            `/StockMovements/stock-balance/${companyId}`,
            { params: { productId: p.id, branchId, stockId: form.stockId } }
          );
          const availableQty = parseFloat(response.data ?? 0);
          if (!settings.allowNegativeSales && availableQty <= 0) {
            toast.error(t('invoice.messages.quantityNotAvailable'));
            return;
          }
        } catch {
          if (!settings.allowNegativeSales) {
            toast.warning(t('invoice.messages.cannotVerifyQuantity'));
            return;
          }
        }
      }

      const unitsList = Array.isArray(p.units) ? p.units : [];
      const baseUnit = chosenUnit || unitsList.find(u => u.isBaseUnit) || {};
      const line = {
        productId: p.id,
        productName: p?.arabicName ?? p?.nameAr ?? p?.name ?? "",
        description: p?.description ?? "",
        unitId: baseUnit?.unitId || 0,
        unitName: baseUnit?.unitNameAr || "",
        qty: parseFloat(1).toFixed(decimalPlaces),
        price: pickUnitPriceForDirection(baseUnit, p),
        discount: parseFloat(0).toFixed(decimalPlaces),
        taxRate: parseFloat(p?.taxRate || 0).toFixed(decimalPlaces),
        unitsList,
      };

      setForm(f => ({ ...f, lines: [...f.lines, line] }));
      toast.success("✅ تم إدراج الصنف عبر الباركود");
    } finally {
      setBarcode("");
      setBarcodeLoading(false);
    }
  }

  function handleBarcodeKeyDown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      insertProductByBarcode(barcode);
    }
  }

  useEffect(() => {
    if (!companyId) return;
    axios.get(`/InvoiceTypes?companyId=${companyId}`).then(r => setTypes(r.data));
    axios.get(`/Customer/by-company/${companyId}`).then(r => setCustomers(r.data));
    axios.get(`/Supplier/by-company/${companyId}`).then(r => setSuppliers(r.data));
    axios.get(`/PaymentMethod/by-company/${companyId}`).then(r => setMethods(r.data));
    axios.get(`/Safe/by-company/${companyId}`).then(r => setSafes(r.data));
    axios.get(`/Bank/by-company/${companyId}`).then(r => setBanks(r.data));
    axios.get(`/Stock/by-company/${companyId}`).then(r => setStocks(r.data));
    axios.get(`/Product/by-company/${companyId}`).then(r => setProducts(r.data));
  }, [companyId]);

  const handleCustomizeLayout = () => {
    Swal.fire({
      title: t('invoice.titles.customizeAppearance'),
      html: `
        <h3 style="text-align:right">${t('invoice.titles.invoiceHeader')}</h3>
        ${Object.keys(visibleFields)
          .map(
            (f) => `
              <label style="display:block;text-align:right;margin:4px 0">
                <input type="checkbox" id="field-${f}" ${visibleFields[f] ? "checked" : ""}/>
                ${fieldLabels[f] || f}
              </label>`
          )
          .join("")}
        <hr/>
        <h3 style="text-align:right">${t('invoice.titles.invoiceLines')}</h3>
        ${Object.keys(visibleItemFields)
          .map(
            (f) => `
              <label style="display:block;text-align:right;margin:4px 0">
                <input type="checkbox" id="item-${f}" ${visibleItemFields[f] ? "checked" : ""}/>
                ${fieldLabels[f] || f}
              </label>`
          )
          .join("")}
      `,
      confirmButtonText: t('common.save'),
      preConfirm: () => {
        const updatedHeader = { ...visibleFields };
        Object.keys(updatedHeader).forEach((f) => {
          updatedHeader[f] = document.getElementById(`field-${f}`).checked;
        });

        const updatedItems = { ...visibleItemFields };
        Object.keys(updatedItems).forEach((f) => {
          updatedItems[f] = document.getElementById(`item-${f}`).checked;
        });

        setVisibleFields(updatedHeader);
        setVisibleItemFields(updatedItems);

        handleSaveLayout(updatedHeader, updatedItems);
      },
    });
  };

  useEffect(() => {
    if (!companyId || !form.invoiceTypeId) return;

    axios
      .get(`/InvoiceLayout/${companyId}/${form.invoiceTypeId}`)
      .then((res) => {
        setLayout(res.data);
        const parser = new DOMParser();

        const headerXml = parser.parseFromString(res.data.headerLayoutXml, "text/xml");
        const headerFields = {};
        headerXml.querySelectorAll("Field").forEach((f) => {
          headerFields[f.getAttribute("name")] = f.getAttribute("visible") === "true";
        });
        setVisibleFields((prev) => ({ ...prev, ...headerFields }));

        const itemsXml = parser.parseFromString(res.data.itemsLayoutXml, "text/xml");
        const itemFields = {};
        itemsXml.querySelectorAll("Field").forEach((f) => {
          itemFields[f.getAttribute("name")] = f.getAttribute("visible") === "true";
        });
        setVisibleItemFields((prev) => ({ ...prev, ...itemFields }));
      })
      .catch(() => {
        console.log("لا يوجد مظهر محفوظ، سيتم استخدام الافتراضي");
      });
  }, [companyId, form.invoiceTypeId]);

  const toggleField = (name) => {
    setVisibleFields((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const handleSaveLayout = async (headerFields, itemFields) => {
    const headerXml = `
    <Layout>
      ${Object.entries(headerFields)
        .map(
          ([name, vis], i) =>
            `<Field name='${name}' label='${fieldLabels[name] || name}' visible='${vis}' order='${i + 1}' />`
        )
        .join("\n")}
    </Layout>`;

    const itemsXml = `
    <Layout>
      ${Object.entries(itemFields)
        .map(
          ([name, vis], i) =>
            `<Field name='${name}' label='${fieldLabels[name] || name}' visible='${vis}' order='${i + 1}' />`
        )
        .join("\n")}
    </Layout>`;

    try {
      await axios.post("/InvoiceLayout/save", {
        companyId,
        invoiceTypeId: form.invoiceTypeId,
        headerLayoutXml: headerXml,
        itemsLayoutXml: itemsXml,
        userName: localStorage.getItem("username") || "system",
      });
      toast.success(t('invoice.messages.appearanceSaved'));
    } catch (err) {
      toast.error(t('invoice.messages.appearanceSaveFailed'));
    }
  };

  const partyForType = (typeId) => {
    if ([1, 8, 3, 10, 5].includes(typeId)) return "customer";
    if ([2, 7, 4, 6,12,13].includes(typeId)) return "supplier";
    // 12 = مناقلة, 13 = تسوية -> No party required usually, or internal
    return null;
  };

  const currentType = types.find(t => t.invoiceTypeId === form.invoiceTypeId);
  const direction = currentType?.direction ?? currentType?.Direction;
  const isInbound = direction === 1;
  const isOutbound = direction === 2;

  const party = partyForType(form.invoiceTypeId);
  const isCustomerParty = party === "customer";
  const isSupplierParty = party === "supplier";

  useEffect(() => {
    const credit = methods.find(m => m.nameAr === "آجل")?.id ?? null;
    setCreditMethodId(credit);
  }, [methods]);

  useEffect(() => {
    const party = partyForType(form.invoiceTypeId);
    setForm(f => ({
      ...f,
      customerId: party === "customer" ? (f.customerId || 0) : 0,
      supplierId: party === "supplier" ? (f.supplierId || 0) : 0,
    }));
  }, [form.invoiceTypeId]);

  const fetchNextInvoiceNumber = async typeId => {
    if (!typeId || mode !== "new" && mode !== "fromQuote") return;
    try {
      const res = await invoiceApi.getNextNumber(companyId, branchId, typeId);
      setForm(f => ({
        ...f,
        invoiceNumber: res.data.toString(),
        issueDate: new Date().toLocaleDateString('en-CA'),
        referenceDate: new Date().toLocaleDateString('en-CA'),
      }));
    } catch (error) {
      console.error(t('invoice.messages.fetchNextNumberFailed'), error);
    }
  };

  useEffect(() => {
    fetchNextInvoiceNumber(form.invoiceTypeId);
  }, [form.invoiceTypeId]);

  useEffect(() => {
    if (!companyId || !form.invoiceTypeId || methods.length === 0) return;

    axios.get("/ProgramSettings/by-company-and-type", {
      params: { companyId, invoiceTypeId: form.invoiceTypeId },
    })
      .then(r => {
        const s = r.data;
        setSettings(s);

        setPriceIncludesVAT(mode === "new" ? !!s.isPriceIncludesVAT : false);

        if (mode === "new") {
          const cashId = methods.find(m => m.nameAr === "نقدي")?.id;
          
          // Determine Safe/Stock IDs: Priority to Restricted > Settings > Default
          const finalSafeId = restrictedSafeId || s.defaultSafeId || f.safeId;
          const finalStockId = restrictedStockId || s.defaultStockId || f.stockId;

          setForm(f => ({
            ...f,
            customerId: s.defaultCustomerId ?? f.customerId,
            paymentMethodId: s.defaultPaymentMethodId ?? f.paymentMethodId,
            safeId: (s.defaultPaymentMethodId ?? f.paymentMethodId) === cashId ? finalSafeId : 0,
            bankId: (s.defaultPaymentMethodId ?? f.paymentMethodId) !== cashId ? (s.defaultBankId ?? f.bankId) : 0,
            stockId: finalStockId,
            invoiceStatus: s.autoApproveInvoices ? 1 : 0,
          }));
        }
      })
      .catch(console.error);
  }, [companyId, form.invoiceTypeId, methods, mode, restrictedSafeId, restrictedStockId]);

  function buildInvoiceLines(rawItems = []) {
    return rawItems.map(it => {
      const pid = Number(it.productId);
      const p = products.find(pr => Number(pr.id) === pid);

      const unitsList = Array.isArray(p?.units) ? p.units : (it.units || []);
      const u = unitsList.find(x => Number(x.unitId) === Number(it.unitId))
        || unitsList.find(x => x.isBaseUnit)
        || {};

      const tax = p?.taxRate ?? it.taxPercent ?? 0;
      let storedPrice = parseFloat(it.unitPrice ?? it.price ?? 0);

      if (priceIncludesVAT) {
        storedPrice = storedPrice * (1 + parseFloat(tax || 0));
      }

      return {
        productId: pid,
        productName: p?.arabicName ?? p?.nameAr ?? it.description ?? "",
        description: it.description ?? "",
        unitId: u?.unitId || 0,
        unitName: u?.unitNameAr || "",
        qty: parseFloat(it.quantity || 1).toFixed(decimalPlaces),
        price: storedPrice.toFixed(decimalPlaces),
        discount: parseFloat(it.discount || 0).toFixed(decimalPlaces),
        taxRate: parseFloat(tax).toFixed(decimalPlaces),
        unitsList,
      };
    });
  }

  function formatDateForInput(value) {
    if (!value) return "";
    const text = String(value);
    return text.length >= 10 ? text.slice(0, 10) : text;
  }

  useEffect(() => {
    const sourceId =
      mode === "edit" ? editingId :
        mode === "return" ? returnOf :
          mode === "fromQuote" ? fromQuoteId :
            null;

    if (!sourceId) return;
    if (products.length === 0) return;

    invoiceApi.getById(sourceId)
      .then(res => {
        const inv = res.data;
        const rawItems = inv.items || inv.invoiceItems || inv.details || [];
        const lines = buildInvoiceLines(rawItems);

        setForm(f => ({
          ...f,
          invoiceTypeId: mode === "return" ? 3 : mode === "fromQuote" ? 1 : inv.invoiceType,
          invoiceNumber: mode === "fromQuote" ? "" : inv.invoiceNumber,
          issueDate: mode === "fromQuote" ? new Date().toLocaleDateString('en-CA') : formatDateForInput(inv.issueDate),
          referenceNumber: mode === "return" ? inv.invoiceNumber : (inv.referenceNumber ?? inv.invoiceNumber),
          referenceDate: formatDateForInput(mode === "return" ? inv.issueDate : (inv.referenceDate || inv.issueDate)),
          customerId: inv.customerId ?? f.customerId,
          supplierId: mode === "fromQuote" ? 0 : (inv.supplierId ?? 0),
          customerPrevBalance: parseFloat(inv.customerPrevBalance || 0),
          stockId: inv.stockId ?? f.stockId,
          toStockId: inv.toStockId ?? 0,
          salesmanId: inv.salesmanId ?? f.salesmanId,
          paymentMethodId: inv.paymentMethodId ?? f.paymentMethodId,
          safeId: inv.safeId ?? 0,
          bankId: inv.bankId ?? 0,
          notes: inv.notes || "",
          discount: parseFloat(inv.discountTotal || 0).toFixed(decimalPlaces),
          invoiceStatus: mode === "fromQuote" ? 0 : (inv.invoiceStatus ?? 0),
          paidAmount: mode === "fromQuote" ? 0 : parseFloat(inv.paidAmount || 0),
          journalEntryGlobalId: inv.journalEntryGlobalId, // ✅ Populate from API
          lines,
        }));

        if (mode === "fromQuote") {
          fetchNextInvoiceNumber(1);
        }

        setIsApproved(inv.invoiceStatus === 1);
        setShowApproveButton(mode === "edit" ? inv.invoiceStatus === 0 : false);
        setCanUnapprove(!!inv.canUnapprove);
        setIsZatcaLocked(!!inv.isZatcaLocked);
        setZatcaStatus(inv.zatcaStatus || (inv.isSentToZatca ? "REPORTED" : null));
      })
      .catch(console.error);
  }, [mode, editingId, returnOf, fromQuoteId, products, decimalPlaces, priceIncludesVAT]);

  function pickUnitPriceForDirection(u, p) {
    const sale = u?.saleprice ?? p?.saleprice ?? 0;
    const buy = getPurchasePrice(u, p);
    const base = isInbound ? buy : sale;
    return parseFloat(base || 0).toFixed(decimalPlaces);
  }

  function getPurchasePrice(u, p) {
    return (
      u?.purchprice ??
      u?.purchaseprice ??
      p?.purchprice ??
      p?.purchaseprice ??
      u?.saleprice ??
      p?.saleprice ??
      0
    );
  }

  const calculateTotals = () => {
    let subtotalVisible = 0, totalLineDiscounts = 0, totalTaxableAmount = 0, totalVATCalculated = 0;

    form.lines.forEach(ln => {
      const qty = parseFloat(ln.qty) || 0;
      const price = parseFloat(ln.price) || 0;
      const lineDisc = parseFloat(ln.discount) || 0;
      const taxRate = parseFloat(ln.taxRate) || 0;

      const grossLine = qty * price;
      const netLineAfterDisc = grossLine - lineDisc;

      subtotalVisible += grossLine;
      totalLineDiscounts += lineDisc;

      if (priceIncludesVAT) {
        const taxable = netLineAfterDisc / (1 + taxRate);
        const vat = netLineAfterDisc - taxable;
        totalTaxableAmount += taxable;
        totalVATCalculated += vat;
      } else {
        totalTaxableAmount += netLineAfterDisc;
        totalVATCalculated += netLineAfterDisc * taxRate;
      }
    });

    const docDiscount = parseFloat(form.discount) || 0;
    const finalTaxable = totalTaxableAmount - docDiscount;

    const net = priceIncludesVAT
      ? subtotalVisible - totalLineDiscounts - docDiscount
      : finalTaxable + totalVATCalculated;

    return { subtotal: subtotalVisible, totalLineDiscounts, totalDiscount: docDiscount, totalAfterDiscount: finalTaxable, totalVAT: totalVATCalculated, net };
  };

  const totals = calculateTotals();
  const defaultInvoiceStatus = settings.autoApproveInvoices ? 1 : 0;

  useEffect(() => {
    if (form.paymentMethodId !== creditMethodId) {
      setForm(f => ({
        ...f,
        paidAmount: parseFloat(totals.net.toFixed(decimalPlaces)),
      }));
    }
  }, [form.paymentMethodId, totals.net, decimalPlaces, creditMethodId]);

  function handleChange(e) {
    const { name, value } = e.target;
    let newValue = value;
    if (
      [
        "paymentMethodId",
        "safeId",
        "bankId",
        "customerId",
        "supplierId",
        "stockId",
        "toStockId",
        "salesmanId",
        "invoiceStatus",
        "paidAmount",
      ].includes(name)
    ) {
      newValue = +value;
    } else if (name === "discount") {
      newValue = parseFloat(value).toFixed(decimalPlaces);
    }
    setForm(f => ({ ...f, [name]: newValue }));
  }

  function handleAddLine() {
    setForm(f => ({
      ...f,
      lines: [
        ...f.lines,
        {
          productId: 0,
          productName: "",
          description: "",
          unitId: 0,
          unitName: "",
          qty: parseFloat(1).toFixed(decimalPlaces),
          price: parseFloat(0).toFixed(decimalPlaces),
          discount: parseFloat(0).toFixed(decimalPlaces),
          taxRate: parseFloat(0).toFixed(decimalPlaces),
          unitsList: [],
        },
      ],
    }));
  }

  function handleLineFieldKeyDown(e, rowIndex, field) {
    if (e.key !== "Enter") return;

    e.preventDefault();

    const nextField = getNextLineField(field);
    if (nextField) {
      focusLineField(rowIndex, nextField);
      return;
    }

    const nextRowIndex = form.lines.length;
    handleAddLine();
    pendingLineFocusRef.current = { rowIndex: nextRowIndex, field: getFirstLineField() };
  }

  useEffect(() => {
    if (!pendingLineFocusRef.current) return;

    const { rowIndex, field } = pendingLineFocusRef.current;
    const timer = setTimeout(() => {
      if (focusLineField(rowIndex, field)) {
        pendingLineFocusRef.current = null;
      }
    }, 0);

    return () => clearTimeout(timer);
  }, [form.lines.length, visibleItemFields.ProductId, visibleItemFields.Description, visibleItemFields.Unit, visibleItemFields.Quantity, visibleItemFields.UnitPrice, visibleItemFields.Discount]);

  function handleRemoveLine(i) {
    setForm(f => ({ ...f, lines: f.lines.filter((_, j) => j !== i) }));
  }

  // تبديل اختيار صنف للإدراج المتعدد
  function toggleBulkProduct(productId, checked) {
    setBulkSelected(prev => {
      const next = { ...prev };
      if (checked) {
        if (next[productId] == null) next[productId] = 1;
      } else {
        delete next[productId];
      }
      return next;
    });
  }

  // تحديث كمية صنف محدد في الإدراج المتعدد
  function setBulkProductQty(productId, qtyRaw) {
    const qty = parseFloat(qtyRaw) || 0;
    setBulkSelected(prev => ({ ...prev, [productId]: qty }));
  }

  // تنفيذ الإدراج المتعدد للأصناف المحددة
  function handleBulkInsert() {
    const selectedIds = Object.keys(bulkSelected).map(Number);
    if (selectedIds.length === 0) {
      toast.warning("اختر أصنافًا أولاً");
      return;
    }

    const newLines = selectedIds.map(pid => {
      const p = products.find(pr => Number(pr.id) === pid);
      if (!p) return null;
      const unitsList = Array.isArray(p.units) ? p.units : [];
      const bu = unitsList.find(u => u.isBaseUnit) || {};
      const qty = parseFloat(bulkSelected[pid] ?? 1) || 1;
      return {
        productId: pid,
        productName: p?.arabicName ?? p?.nameAr ?? p?.name ?? "",
        description: p?.description ?? "",
        unitId: bu?.unitId || 0,
        unitName: bu?.unitNameAr || "",
        qty: parseFloat(qty).toFixed(decimalPlaces),
        price: pickUnitPriceForDirection(bu, p),
        discount: parseFloat(0).toFixed(decimalPlaces),
        taxRate: parseFloat(p?.taxRate || 0).toFixed(decimalPlaces),
        unitsList,
      };
    }).filter(Boolean);

    setForm(f => ({ ...f, lines: [...f.lines, ...newLines] }));
    setBulkSelected({});
    setBulkSearch("");
    setBulkOpen(false);
    toast.success("✅ تم إدراج الأصناف المحددة");
  }

  async function handleLineChange(idx, field, raw) {
    const lines = [...form.lines];
    const ln = { ...lines[idx] };

    if (field === "productName") {
      ln.productName = raw;
      const p = products.find(x => x.arabicName === raw);
      if (!p) {
        lines[idx] = ln;
        setForm(f => ({ ...f, lines }));
        return;
      }

      if (isOutbound) {
        try {
          const response = await axios.get(
            `/StockMovements/stock-balance/${companyId}`,
            { params: { productId: p.id, branchId, stockId: form.stockId } }
          );
          const availableQty = parseFloat(response.data ?? 0);
          if (!settings.allowNegativeSales && availableQty <= 0) {
            toast.error(t('invoice.messages.quantityNotAvailable'));
            return;
          }
        } catch {
          if (!settings.allowNegativeSales) {
            toast.warning(t('invoice.messages.cannotVerifyQuantity'));
            return;
          }
        }
      }

      ln.productId = p.id;
      ln.taxRate = parseFloat(p.taxRate || 0).toFixed(decimalPlaces);
      ln.unitsList = p.units || [];
      const bu = ln.unitsList.find(u => u.isBaseUnit) || {};
      ln.unitId = bu.unitId || 0;
      ln.unitName = bu.unitNameAr || "";
      // Default description from product if available
      ln.description = p?.description ?? ln.description ?? "";

      ln.price = pickUnitPriceForDirection(bu, p, ln.taxRate);
      ln.discount = parseFloat(0).toFixed(decimalPlaces);
      ln.qty = parseFloat(1).toFixed(decimalPlaces);
    } else if (field === "unitId") {
      const u = ln.unitsList.find(x => x.unitId === +raw) || {};
      ln.unitId = u.unitId || 0;
      ln.unitName = u.unitNameAr || "";
      ln.price = pickUnitPriceForDirection(u, null, ln.taxRate);
    } else if (field === "qty") {
      // السماح بالقيمة كما هي (نص) للسماح بالكتابة الحرة
      ln.qty = raw;
    } else if (field === "price") {
      // السماح بالقيمة كما هي (نص) للسماح بالكتابة الحرة
      ln.price = raw;
    } else if (field === "discount") {
      // السماح بالقيمة كما هي (نص) للسماح بالكتابة الحرة
      ln.discount = raw;
    } else if (field === "taxRate") {
      ln.taxRate = parseFloat(raw).toFixed(decimalPlaces);
    } else if (field === "description") {
      ln.description = raw;
    }

    lines[idx] = ln;
    setForm(f => ({ ...f, lines }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (viewMode || isSaving) return;

    if (isCustomerParty && (!form.customerId || form.customerId === 0)) {
      toast.error(t('invoice.messages.selectCustomerFirst'));
      return;
    }
    if (isSupplierParty && (!form.supplierId || form.supplierId === 0)) {
      toast.error(t('invoice.messages.selectSupplierFirst'));
      return;
    }

    const cashId = methods.find(m => m.nameAr === "نقدي")?.id;
    if (form.paymentMethodId === cashId && !form.safeId) {
      return alert(t('invoice.messages.selectSafe'));
    }

    const productIds = new Set();
    for (let ln of form.lines) {
      const qty = parseFloat(ln.qty);
      if (!ln.productId || isNaN(qty) || qty <= 0) {
        toast.error(`${t('invoice.messages.invalidQuantity')} "${ln.productName}"`);
        return;
      }
      if (productIds.has(ln.productId)) {
        toast.error(`${t('invoice.messages.duplicateItem')} "${ln.productName}"`);
        return;
      }
      productIds.add(ln.productId);
    }

    if (isOutbound && !settings.allowNegativeSales) {
      let hasError = false;
      for (let ln of form.lines) {
        const qty = parseFloat(ln.qty);
        try {
          const res = await axios.get(
            `/StockMovements/stock-balance/${companyId}`,
            { params: { productId: ln.productId, branchId, stockId: form.stockId } }
          );
          const availableQty = parseFloat(res.data ?? 0);
          if (qty > availableQty) {
            toast.error(`${t('invoice.messages.insufficientQuantity')} "${ln.productName}" (${availableQty})`);
            hasError = true;
          }
        } catch {
          toast.error(`${t('invoice.messages.cannotCheckBalance')} "${ln.productName}"`);
          hasError = true;
        }
      }
      if (hasError) return;
    }

    const payloadItems = form.lines.map(ln => {
      let unitPriceForAPI = parseFloat(ln.price);
      if (priceIncludesVAT) {
        unitPriceForAPI = unitPriceForAPI / (1 + parseFloat(ln.taxRate));
      }
      return {
        productId: ln.productId,
        unitId: ln.unitId,
        quantity: parseFloat(ln.qty),
        unitPrice: unitPriceForAPI,
        discount: parseFloat(ln.discount),
        taxPercent: parseFloat(ln.taxRate),
        description: ln.description ?? "",
      };
    });

    let invoiceStatusToSend = form.invoiceStatus;
    if (mode === "new" && settings.autoApproveInvoices) {
      invoiceStatusToSend = 1;
    }

    const payload = {
      companyId,
      branchId,
      userId: userId || 0, // ✅ Send userId
      invoiceTypeId: form.invoiceTypeId,
      invoiceNumber: form.invoiceNumber,
      issueDate: new Date(form.issueDate).toISOString(),
      referenceNumber: form.referenceNumber,
      referenceDate: new Date(form.referenceDate).toISOString(),
      customerId: isCustomerParty ? form.customerId : 0,
      supplierId: isSupplierParty ? form.supplierId : 0,
      customerPrevBalance: parseFloat(form.customerPrevBalance),
      stockId: form.stockId,
      toStockId: form.invoiceTypeId === 12 ? form.toStockId : null,
      salesmanId: form.salesmanId,
      paymentMethodId: form.paymentMethodId,
      safeId: form.safeId,
      bankId: form.bankId,
      notes: form.notes,
      discount: parseFloat(form.discount),
      invoiceStatus: invoiceStatusToSend,
      paidAmount: parseFloat(form.paidAmount),
      totals: {
        subtotal: totals.subtotal,
        totalAfterDiscount: totals.totalAfterDiscount,
        totalVAT: totals.totalVAT,
        net: totals.net,
      },
      items: payloadItems,
    };

    if (mode === "edit") {
      payload.InvGlobalID = editingId;
    }

    try {
      setIsSaving(true);

      if (mode === "return") {
        payload.invoiceTypeId = 3;
        payload.referenceNumber = form.referenceNumber || form.invoiceNumber;
        payload.referenceDate = new Date().toISOString();
      }

      console.log("🚀 Sending payload:", payload, "mode:", mode);
      const res =
        mode === "edit"
          ? await invoiceApi.update(editingId, payload)
          : await invoiceApi.create(payload);

      const invGlobalId = mode === "edit" ? editingId : res.data.invGlobalID;
      const canApproveAfterSave = invoiceStatusToSend !== 1;

      if (settings.autoPrintAfterSave) {
        toast.success(t('invoice.messages.invoiceSaved'));
        setIsSaving(false);
        // الطباعة المباشرة (فتح نافذة الطباعة تلقائياً)
        window.open(`/invoices/print/${invGlobalId}?auto=true`, "_blank");
        
        // إذا كان هناك اعتماد تلقائي، لا حاجة لفعله هنا لأنه تم إرسال الحالة في الـ payload
        // فقط نقوم بتصفير النموذج
        resetForm();
      } else {
        setIsSaving(false);
        const result = await Swal.fire({
          title: t('invoice.messages.invoiceSaved'),
          showCancelButton: true,
          showDenyButton: canApproveAfterSave,
          cancelButtonText: t('invoice.buttons.new'),
          confirmButtonText: t('invoice.buttons.print'),
          denyButtonText: canApproveAfterSave ? t('invoice.buttons.approveInvoice') : undefined,
        });

        if (result.isConfirmed) {
          window.open(`/invoices/print/${invGlobalId}`, "_blank");
          resetForm();
        } else if (result.isDenied && canApproveAfterSave) {
          await handleApprove(invGlobalId);
          resetForm();
        } else {
          resetForm();
        }
      }
    } catch (err) {
      setIsSaving(false);
      console.error("❌ Save error:", err);

      const serverError =
        err.response?.data?.errors ||
        err.response?.data ||
        err.message;
      toast.error(
        t('invoice.messages.saveError') + ": " + JSON.stringify(serverError)
      );
    }
  }

  // تطبيق الإعدادات الافتراضية من إعدادات البرنامج لنوع الفاتورة المحدد
  async function applyProgramDefaults(typeId) {
    if (!companyId || !typeId) return;
    try {
      const r = await axios.get("/ProgramSettings/by-company-and-type", {
        params: { companyId, invoiceTypeId: typeId },
      });
      const s = r.data;
      setSettings(s);

      const cashIdLocal = methods.find(m => m.nameAr === "نقدي")?.id;
      const defaultPM = s.defaultPaymentMethodId ?? 0;
      setPriceIncludesVAT(!!s.isPriceIncludesVAT);

      // Priority: Restricted Single > Settings > Default
      // New Logic:
      // If allowedSafeIds has items, check if default safe is in it. If not, pick first allowed.
      // If restrictedSafeId (legacy single) exists, it overrides everything.
      
      let finalSafeId = s.defaultSafeId || 0;
      if (restrictedSafeId) {
          finalSafeId = restrictedSafeId;
      } else if (allowedSafeIds.length > 0) {
          // If default is not allowed, pick first allowed
          if (!allowedSafeIds.includes(finalSafeId)) {
              finalSafeId = allowedSafeIds[0];
          }
      }

      let finalStockId = s.defaultStockId || 0;
      if (restrictedStockId) {
          finalStockId = restrictedStockId;
      } else if (allowedStockIds.length > 0) {
          if (!allowedStockIds.includes(finalStockId)) {
              finalStockId = allowedStockIds[0];
          }
      }

      setForm(f => ({
        ...f,
        customerId: s.defaultCustomerId ?? 0,
        paymentMethodId: defaultPM || 0,
        safeId: defaultPM === cashIdLocal ? finalSafeId : 0,
        bankId: defaultPM !== cashIdLocal ? (s.defaultBankId ?? 0) : 0,
        stockId: finalStockId,
        invoiceStatus: mode === "new" ? (s.autoApproveInvoices ? 1 : 0) : f.invoiceStatus,
      }));
    } catch (err) {
      console.error("Failed to apply program defaults", err);
    }
  }

  const handleApprove = async (idToApprove = editingId) => {
    try {
      setIsSaving(true);
      await invoiceApi.approve(idToApprove);
      toast.success(t('invoice.messages.invoiceApproved'));
      setShowApproveButton(false);
      setIsApproved(true);
      setCanUnapprove(!isZatcaLocked);
      setForm(f => ({ ...f, invoiceStatus: 1 }));
      
      // Update Zatca Status locally to reflect success immediately
      setZatcaStatus("REPORTED"); 
    } catch {
      toast.error(t('invoice.messages.approvalFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleUnapprove = async () => {
    if (!editingId || isSaving || !canUnapprove) return;

    const result = await Swal.fire({
      title: "إلغاء اعتماد الفاتورة؟",
      text: "سيتم إرجاع الفاتورة إلى مسودة حتى تتمكن من تعديلها.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "نعم، إلغاء الاعتماد",
      cancelButtonText: "إلغاء",
    });

    if (!result.isConfirmed) return;

    try {
      setIsSaving(true);
      await invoiceApi.unapprove(editingId);
      const res = await invoiceApi.getById(editingId);
      const inv = res.data;
      const rawItems = inv.items || inv.invoiceItems || inv.details || [];
      const lines = buildInvoiceLines(rawItems);

      setForm(f => ({
        ...f,
        invoiceTypeId: inv.invoiceType,
        invoiceNumber: inv.invoiceNumber,
        issueDate: formatDateForInput(inv.issueDate),
        referenceNumber: inv.referenceNumber ?? inv.invoiceNumber,
        referenceDate: formatDateForInput(inv.referenceDate || inv.issueDate),
        customerId: inv.customerId ?? f.customerId,
        supplierId: inv.supplierId ?? 0,
        customerPrevBalance: parseFloat(inv.customerPrevBalance || 0),
        stockId: inv.stockId ?? f.stockId,
        toStockId: inv.toStockId ?? 0,
        salesmanId: inv.salesmanId ?? f.salesmanId,
        paymentMethodId: inv.paymentMethodId ?? f.paymentMethodId,
        safeId: inv.safeId ?? 0,
        bankId: inv.bankId ?? 0,
        notes: inv.notes || "",
        discount: parseFloat(inv.discountTotal || 0).toFixed(decimalPlaces),
        invoiceStatus: inv.invoiceStatus ?? 0,
        paidAmount: parseFloat(inv.paidAmount || 0),
        journalEntryGlobalId: inv.journalEntryGlobalId ?? null,
        lines,
      }));

      setShowApproveButton(inv.invoiceStatus === 0);
      setIsApproved(inv.invoiceStatus === 1);
      setCanUnapprove(!!inv.canUnapprove);
      setIsZatcaLocked(!!inv.isZatcaLocked);
      setZatcaStatus(inv.zatcaStatus || (inv.isSentToZatca ? "REPORTED" : null));
      toast.success("✅ تم إلغاء اعتماد الفاتورة وإرجاعها إلى مسودة");
    } catch (err) {
      toast.error(err?.response?.data || "❌ فشل إلغاء اعتماد الفاتورة");
    } finally {
      setIsSaving(false);
    }
  };

  const handleShowZatcaDetails = () => {
    // Here we could fetch details if not already loaded, 
    // or just show a message for now since we have the status.
    Swal.fire({
      title: 'حالة الربط مع الهيئة',
      text: zatcaStatus === "REPORTED" ? "تم إرسال الفاتورة بنجاح ✅" : "لم يتم الإرسال بعد أو يوجد خطأ",
      icon: zatcaStatus === "REPORTED" ? 'success' : 'info'
    });
  };

  function resetForm() {
    setEditingId(null);
    const newType = form.invoiceTypeId || initialType || 0;
    setForm({ ...initialFormState, invoiceTypeId: newType, invoiceStatus: defaultInvoiceStatus });
    fetchNextInvoiceNumber(newType);
    setShowApproveButton(false);
    setIsApproved(false);
    setCanUnapprove(false);
    setIsZatcaLocked(false);
    setZatcaStatus(null);
    applyProgramDefaults(newType);

    // Apply restricted values if present even without program defaults
    let forcedStockId = form.stockId;
    if (restrictedStockId) forcedStockId = restrictedStockId;
    else if (allowedStockIds.length > 0 && !allowedStockIds.includes(form.stockId)) forcedStockId = allowedStockIds[0];

    let forcedSafeId = form.safeId;
    if (restrictedSafeId) forcedSafeId = restrictedSafeId;
    else if (allowedSafeIds.length > 0 && !allowedSafeIds.includes(form.safeId)) forcedSafeId = allowedSafeIds[0];

    setForm(f => ({
        ...f,
        stockId: forcedStockId,
        safeId: forcedSafeId
    }));
    
    navigate(`/invoices/new/${newType}`, { replace: true });
  }

  // التنقل بين السجلات عبر API
  async function goToInvoice(action) {
    try {
      let res;
      const currentInvoiceId = editingId || id;

       if (action === 'last') {
        res = await invoiceApi.getLast(companyId, form.invoiceTypeId);

            } else if (action === 'previous') {
        if (currentInvoiceId) {
          res = await invoiceApi.getPrevious(currentInvoiceId);
        } else {
          // عند عدم وجود id في وضع فاتورة جديدة، اعتبر "السابق" هو الأخير
          res = await invoiceApi.getLast(companyId, form.invoiceTypeId);
        }
      } else if (action === 'next') {
        if (currentInvoiceId) {
          res = await invoiceApi.getNext(currentInvoiceId);
        } else {
          // عند عدم وجود id في وضع فاتورة جديدة، اعتبر "التالي" هو الأول
          res = await invoiceApi.getFirst(companyId, form.invoiceTypeId);
        }
      } else if (action === 'first') {
        res = await invoiceApi.getFirst(companyId, form.invoiceTypeId);

      }

      const inv = res?.data;
      const nextId = inv?.invGlobalID || inv?.id || inv?.InvGlobalID;
      if (nextId) {
        // تعبئة التفاصيل فورًا كما في وضع التعديل
        const rawItems = inv.items || inv.invoiceItems || inv.details || [];
        const lines = buildInvoiceLines(rawItems);

        setForm(f => ({
          ...f,
          invoiceTypeId: inv.invoiceType,
          invoiceNumber: inv.invoiceNumber,
          issueDate: formatDateForInput(inv.issueDate),
          referenceNumber: inv.referenceNumber ?? inv.invoiceNumber,
          referenceDate: formatDateForInput(inv.referenceDate || inv.issueDate),
          customerId: inv.customerId ?? f.customerId,
          supplierId: inv.supplierId ?? 0,
          customerPrevBalance: parseFloat(inv.customerPrevBalance || 0),
          stockId: inv.stockId ?? f.stockId,
          toStockId: inv.toStockId ?? 0,
          salesmanId: inv.salesmanId ?? f.salesmanId,
          paymentMethodId: inv.paymentMethodId ?? f.paymentMethodId,
          safeId: inv.safeId ?? 0,
          bankId: inv.bankId ?? 0,
          notes: inv.notes || "",
          discount: parseFloat(inv.discountTotal || 0).toFixed(decimalPlaces),
          invoiceStatus: inv.invoiceStatus ?? 0,
          paidAmount: parseFloat(inv.paidAmount || 0),
          journalEntryGlobalId: inv.journalEntryGlobalId ?? null,
          lines,
        }));

        setShowApproveButton(inv.invoiceStatus === 0);
        setIsApproved(inv.invoiceStatus === 1);
        setCanUnapprove(!!inv.canUnapprove);
        setIsZatcaLocked(!!inv.isZatcaLocked);

        setEditingId(nextId);
        navigate(`/invoices/edit/${nextId}`);
      } else {
        toast.error('لا توجد بيانات للتنقل');
      }
    } catch (err) {
      toast.error('حدث خطأ أثناء التنقل بين الفواتير');
    }
  }
  const getApprovePermission = (typeId) => {
    switch (typeId) {
      case 1: return PERMISSIONS.INVOICE_SALES_APPROVE;
      case 2: return PERMISSIONS.INVOICE_PURCHASE_APPROVE;
      case 3: return PERMISSIONS.INVOICE_SALES_RETURN_APPROVE;
      case 4: return PERMISSIONS.INVOICE_PURCHASE_RETURN_APPROVE;
      case 7: return PERMISSIONS.INVOICE_OPENING_STOCK_APPROVE;
      case 8: return PERMISSIONS.INVOICE_STOCK_IN_APPROVE;
      case 9: return PERMISSIONS.INVOICE_STOCK_OUT_APPROVE;
      case 12: return PERMISSIONS.STOCK_MOVE; // مناقلة
      case 13: return PERMISSIONS.STOCK_MOVE; // تسوية
      default: return null;
    }
  };

  const cashId = methods.find(m => m.nameAr === "نقدي")?.id;
  const isCash = form.paymentMethodId === cashId;
  const isCredit = form.paymentMethodId === creditMethodId;
  const isNonCash = !isCash && !isCredit;

  const handleExportExcel = () => {
    const headerData = [
      [t('invoice.fields.invoiceType'), types.find(t => t.invoiceTypeId === form.invoiceTypeId)?.name || ""],
      [t('invoice.fields.invoiceNumber'), form.invoiceNumber],
      [t('invoice.fields.invoiceDate'), form.issueDate ? form.issueDate.slice(0, 10) : ""],
      [t('invoice.fields.customer'), customers.find(c => c.id === form.customerId)?.name || ""],
      [t('invoice.fields.notes'), form.notes],
      [], 
    ];

    const tableHeader = [
      "#",
      t('invoice.fields.item') || "الصنف",
      t('invoice.fields.description') || "الوصف",
      t('invoice.fields.unit') || "الوحدة",
      t('invoice.fields.quantity') || "الكمية",
      t('invoice.fields.price') || "السعر",
      t('invoice.fields.discount') || "الخصم",
      t('invoice.fields.taxPercent') || "الضريبة %",
      t('invoice.fields.total') || "المجموع",
      t('invoice.fields.net') || "الصافي"
    ];

    const itemsData = form.lines.map((ln, i) => {
        const qty = parseFloat(ln.qty) || 0;
        const price = parseFloat(ln.price) || 0;
        const discount = parseFloat(ln.discount) || 0;
        const taxRate = parseFloat(ln.taxRate) || 0;

        let lineTotalBeforeDiscount = qty * price;
        let lineAfterDiscount = lineTotalBeforeDiscount - discount;
        
        let taxable = lineAfterDiscount;
        let vat = 0;

        if (priceIncludesVAT) {
            taxable = lineAfterDiscount / (1 + taxRate);
            vat = lineAfterDiscount - taxable;
        } else {
            vat = lineAfterDiscount * taxRate;
        }
        
        const net = priceIncludesVAT ? lineAfterDiscount : lineAfterDiscount + vat;

        return [
            i + 1,
            ln.productName,
            ln.description,
            ln.unitName,
            qty,
            price,
            discount,
            (taxRate * 100).toFixed(2) + "%",
            lineTotalBeforeDiscount.toFixed(decimalPlaces),
            net.toFixed(decimalPlaces)
        ];
    });

    const footerData = [
        [],
        ["", "", "", "", "", "", "", t('invoice.summary.subtotal') || "المجموع الفرعي", totals.subtotal.toFixed(decimalPlaces)],
        ["", "", "", "", "", "", "", t('invoice.summary.invoiceDiscount') || "خصم الفاتورة", form.discount],
        ["", "", "", "", "", "", "", t('invoice.summary.vat') || "الضريبة", totals.totalVAT.toFixed(decimalPlaces)],
        ["", "", "", "", "", "", "", t('invoice.summary.net') || "الصافي", totals.net.toFixed(decimalPlaces)]
    ];

    const wsData = [...headerData, tableHeader, ...itemsData, ...footerData];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    ws['!cols'] = [
        { wch: 5 }, { wch: 30 }, { wch: 20 }, { wch: 10 }, { wch: 10 }, 
        { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 15 }, { wch: 15 }
    ];

    XLSX.utils.book_append_sheet(wb, ws, "Invoice");
    XLSX.writeFile(wb, `Invoice_${form.invoiceNumber || 'New'}.xlsx`);
  };

  return (
  <div dir="rtl" style={{ minHeight: '100vh', background: '#f1f5f9', fontFamily: 'inherit', position: 'relative' }}>
    {isSaving && (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(255,255,255,0.72)', backdropFilter: 'blur(2px)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '12px' }}>
        <div style={{ width: '52px', height: '52px', border: '4px solid #dbeafe', borderTopColor: '#2563eb', borderRadius: '9999px', animation: 'spin 0.8s linear infinite' }} />
        <div style={{ color: '#1e3a8a', fontWeight: '800', fontSize: '15px' }}>جاري حفظ الفاتورة، يرجى الانتظار...</div>
      </div>
    )}

    {/* ══ HEADER ══ */}
    <div style={{ background: 'linear-gradient(to left, #1d4ed8, #2563eb)', padding: isMobile ? '12px 14px' : '0 20px', minHeight: isMobile ? 'auto' : '56px', display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'space-between', gap: isMobile ? '10px' : '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>

      {/* Right: Title */}
      <div style={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: isMobile ? 'space-between' : 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
        <h1 style={{ color: 'white', fontSize: isMobile ? '16px' : '18px', fontWeight: '800', margin: 0, whiteSpace: isMobile ? 'normal' : 'nowrap' }}>
          {mode === "view" ? t('invoice.titles.viewInvoice')
            : mode === "edit" ? t('invoice.titles.editInvoice')
            : mode === "return" ? t('invoice.titles.returnInvoice')
            : t('invoice.titles.newInvoice')}
        </h1>
        {zatcaStatus === "REPORTED" && (
          <span onClick={handleShowZatcaDetails} style={{ background: '#10b981', color: 'white', fontSize: '11px', padding: '2px 8px', borderRadius: '999px', fontWeight: '700', cursor: 'pointer' }}>✅ ZATCA</span>
        )}
        {zatcaStatus === "FAILED" && (
          <span onClick={handleShowZatcaDetails} style={{ background: '#ef4444', color: 'white', fontSize: '11px', padding: '2px 8px', borderRadius: '999px', fontWeight: '700', cursor: 'pointer' }}>❌ ZATCA</span>
        )}
      </div>

      {/* Center: Type + Number */}
      <div style={{ background: 'white', borderRadius: '12px', padding: isMobile ? '8px 12px' : '6px 20px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: isMobile ? '12px' : '16px', alignSelf: isMobile ? 'stretch' : 'auto', boxShadow: '0 2px 6px rgba(0,0,0,0.1)' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '14px', color: '#1e293b', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('invoice.fields.invoiceType')}</div>
          <div style={{ fontSize: '14px', fontWeight: '800', color: '#2563eb' }}>{types.find(t => t.invoiceTypeId === form.invoiceTypeId)?.name || '—'}</div>
        </div>
        <div style={{ width: '1px', height: '32px', background: '#e2e8f0' }} />
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '14px', color: '#1e293b', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('invoice.fields.invoiceNumber')}</div>
          <div style={{ fontSize: isMobile ? '18px' : '22px', fontWeight: '900', color: '#2563eb', lineHeight: 1 }}>{form.invoiceNumber || '—'}</div>
        </div>
      </div>

      <Button type="button" onClick={togglePriceIncludesVAT} title={t('programSettings.priceIncludesVAT')} className={(priceIncludesVAT ? "bg-blue-100 text-green-700 border-green-300" : "bg-amber-100 text-amber-700 border-amber-300") + " h-9 px-3 rounded-md text-xs font-bold border"} style={{ alignSelf: isMobile ? 'stretch' : 'auto', width: isMobile ? '100%' : 'auto' }}>
  {priceIncludesVAT ? t('programSettings.priceIncludesVAT') : t('pos.policy.priceExcludesVAT')}
    </Button>

      {/* Left: Nav buttons */}
      <div style={{ display: 'flex', gap: '6px', alignSelf: isMobile ? 'stretch' : 'auto', justifyContent: isMobile ? 'space-between' : 'flex-start' }}>
        {[['last','⏭'],['next','▶'],['previous','◀'],['first','⏮']].map(([action, icon]) => (
          <button key={action} type="button" onClick={() => goToInvoice(action)}
            style={{ width: '36px', height: '36px', background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '8px', color: 'white', fontSize: '14px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
            onMouseOut={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}>
            {icon}
          </button>
        ))}
      </div>
    </div>

    {/* ══ BODY ══ */}
    <div style={{ padding: isMobile ? '12px' : '16px 20px' }}>
      <form onSubmit={handleSubmit}>
        {editingId && isApproved && (
          <div style={{ marginBottom: '12px', background: isZatcaLocked ? '#fef2f2' : '#eff6ff', color: isZatcaLocked ? '#991b1b' : '#1d4ed8', border: `1px solid ${isZatcaLocked ? '#fecaca' : '#bfdbfe'}`, borderRadius: '12px', padding: '12px 14px', fontSize: '14px', fontWeight: '700' }}>
            {isZatcaLocked
              ? 'هذه الفاتورة معتمدة وربط زاتكا مفعل، لذلك لا يمكن تعديلها أو إلغاء اعتمادها.'
              : 'هذه الفاتورة معتمدة. لإجراء التعديل، قم أولًا بإلغاء الاعتماد لإرجاعها إلى مسودة.'}
          </div>
        )}

        {/* ─ Section 1: Header Fields ─ */}
        <div style={{ background: 'white', borderRadius: '16px', padding: isMobile ? '14px' : '20px', marginBottom: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0' }}>

          {/* Row 1: Main fields */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(160px, 1fr))', gap: isMobile ? '12px' : '16px' }}>

            {/* Date */}
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#64748b', marginBottom: '6px' }}>{t('invoice.fields.invoiceDate')}</label>
              <input type="date" name="issueDate" value={form.issueDate} onChange={handleChange} readOnly={viewMode}
                style={{ width: '100%', height: '42px', border: '2px solid #e2e8f0', borderRadius: '10px', padding: '0 12px', fontSize: '14px', fontWeight: '700', background: '#f8fafc', outline: 'none', boxSizing: 'border-box', color: '#1e293b' }} />
            </div>

            {/* Warehouse */}
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#64748b', marginBottom: '6px' }}>
                {form.invoiceTypeId === 12 ? (t('invoice.fields.fromWarehouse') || 'من مستودع') : t('invoice.fields.warehouse')}
              </label>
              <select name="stockId" value={form.stockId} onChange={handleChange}
                disabled={viewMode || !!restrictedStockId}
                style={{ width: '100%', height: '42px', border: '2px solid #e2e8f0', borderRadius: '10px', padding: '0 12px', fontSize: '14px', fontWeight: '700', background: '#f8fafc', outline: 'none', boxSizing: 'border-box', color: '#1e293b' }}>
                <option value={0}>{t('invoice.options.selectWarehouse')}</option>
                {stocks.filter(s => {
                  if (restrictedStockId) return s.id === restrictedStockId;
                  if (allowedStockIds.length > 0) return allowedStockIds.includes(s.id);
                  return true;
                }).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>

            {/* To Warehouse */}
            {form.invoiceTypeId === 12 && (
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#64748b', marginBottom: '6px' }}>{t('invoice.fields.toWarehouse') || 'إلى مستودع'}</label>
                <select name="toStockId" value={form.toStockId} onChange={handleChange} disabled={viewMode}
                  style={{ width: '100%', height: '42px', border: '2px solid #e2e8f0', borderRadius: '10px', padding: '0 12px', fontSize: '14px', fontWeight: '700', background: '#f8fafc', outline: 'none', boxSizing: 'border-box', color: '#1e293b' }}>
                  <option value={0}>{t('invoice.options.selectToWarehouse')}</option>
                  {stocks.filter(s => s.id !== form.stockId).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            )}

            {/* Payment Method */}
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#64748b', marginBottom: '6px' }}>{t('invoice.fields.paymentMethod')}</label>
              <select name="paymentMethodId" value={form.paymentMethodId} onChange={handleChange} disabled={viewMode}
                style={{ width: '100%', height: '42px', border: '2px solid #e2e8f0', borderRadius: '10px', padding: '0 12px', fontSize: '14px', fontWeight: '700', background: '#f8fafc', outline: 'none', boxSizing: 'border-box', color: '#1e293b' }}>
                <option value={0}>{t('invoice.options.selectPaymentMethod')}</option>
                {methods.map(m => <option key={m.id} value={m.id}>{m.nameAr}</option>)}
              </select>
            </div>

            {/* Safe */}
            {isCash && (
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#64748b', marginBottom: '6px' }}>{t('invoice.fields.safe')}</label>
                <select name="safeId" value={form.safeId} onChange={handleChange}
                  disabled={viewMode || !!restrictedSafeId}
                  style={{ width: '100%', height: '42px', border: '2px solid #e2e8f0', borderRadius: '10px', padding: '0 12px', fontSize: '14px', fontWeight: '700', background: '#f8fafc', outline: 'none', boxSizing: 'border-box', color: '#1e293b' }}>
                  <option value={0}>{t('invoice.options.selectSafe')}</option>
                  {safes.filter(s => {
                    if (restrictedSafeId) return s.safeId === restrictedSafeId;
                    if (allowedSafeIds.length > 0) return allowedSafeIds.includes(s.safeId);
                    return true;
                  }).map(s => <option key={s.safeId} value={s.safeId}>{s.name}</option>)}
                </select>
              </div>
            )}

            {/* Bank */}
            {isNonCash && (
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#64748b', marginBottom: '6px' }}>{t('invoice.fields.bank')}</label>
                <select name="bankId" value={form.bankId} onChange={handleChange} disabled={viewMode}
                  style={{ width: '100%', height: '42px', border: '2px solid #e2e8f0', borderRadius: '10px', padding: '0 12px', fontSize: '14px', fontWeight: '700', background: '#f8fafc', outline: 'none', boxSizing: 'border-box', color: '#1e293b' }}>
                  <option value={0}>{t('invoice.options.selectBank')}</option>
                  {banks.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
            )}

            {/* Status */}
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#64748b', marginBottom: '6px' }}>{t('invoice.fields.invoiceStatus')}</label>
              <select name="invoiceStatus" value={form.invoiceStatus} onChange={handleChange} disabled={viewMode}
                style={{ width: '100%', height: '42px', border: '2px solid #e2e8f0', borderRadius: '10px', padding: '0 12px', fontSize: '14px', fontWeight: '700', background: '#f8fafc', outline: 'none', boxSizing: 'border-box', color: '#1e293b' }}>
                <option value={0}>{t('invoice.options.draft')}</option>
                <option value={1}>{t('invoice.options.approved')}</option>
                <option value={2}>{t('invoice.options.paid')}</option>
              </select>
            </div>

            {/* Supplier */}
            {isSupplierParty && (
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#64748b', marginBottom: '6px' }}>{t('invoice.fields.supplier')}</label>
                <select name="supplierId" value={form.supplierId} onChange={handleChange} disabled={viewMode}
                  style={{ width: '100%', height: '42px', border: '2px solid #e2e8f0', borderRadius: '10px', padding: '0 12px', fontSize: '14px', fontWeight: '700', background: '#f8fafc', outline: 'none', boxSizing: 'border-box', color: '#1e293b' }}>
                  <option value={0}>{t('invoice.options.selectSupplier')}</option>
                  {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            )}
          </div>

          {/* Row 2: Customer (full width row) */}
          {isCustomerParty && (
  <div style={{
    marginTop: '16px',
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : (form.customerId !== 0 ? '1fr 1fr auto' : '1fr 1fr'),
    gap: isMobile ? '12px' : '16px',
    alignItems: 'end'
  }}>
    {/* العميل */}
    <div>
      <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#64748b', marginBottom: '6px' }}>{t('invoice.fields.customer')}</label>
      <div style={{ display: 'flex', gap: '8px', flexDirection: isMobile ? 'column' : 'row' }}>
        <Autocomplete
          options={customers}
          getOptionLabel={(o) => o.name || ""}
          value={customers.find(c => c.id === form.customerId) || null}
          onChange={(e, v) => handleChange({ target: { name: 'customerId', value: v ? v.id : 0 } })}
          disabled={viewMode}
          style={{ flex: 1, width: '100%' }}
          size="small"
          renderInput={(params) => (
            <TextField {...params} placeholder={t('invoice.options.selectCustomer')} size="small"
              sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#f8fafc", borderRadius: "10px", fontWeight: "700", fontSize: "14px", height: "42px", "& fieldset": { borderWidth: "2px", borderColor: "#e2e8f0" } } }} />
          )}
        />
        {!viewMode && (
          <button type="button" onClick={() => setOpenCustomerModal(true)}
            style={{ width: isMobile ? '100%' : '42px', height: '42px', background: '#2563eb', border: 'none', borderRadius: '10px', color: 'white', fontSize: '20px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            +
          </button>
        )}
      </div>
    </div>

    {/* الملاحظات - أصبحت بنفس الصف */}
    <div>
      <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#64748b', marginBottom: '6px' }}>{t('invoice.fields.notes')}</label>
      <input name="notes" value={form.notes} onChange={handleChange} readOnly={viewMode}
        placeholder="اكتب ملاحظاتك هنا..."
        style={{ width: '100%', height: '42px', border: '2px solid #e2e8f0', borderRadius: '10px', padding: '0 14px', fontSize: '14px', fontWeight: '600', background: '#f8fafc', outline: 'none', boxSizing: 'border-box', color: '#1e293b' }} />
    </div>

    {/* رصيد العميل السابق */}
    {form.customerId !== 0 && (
      <div style={{ minWidth: isMobile ? '100%' : '160px' }}>
        <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#64748b', marginBottom: '6px' }}>{t('invoice.fields.customerPrevBalance')}</label>
        <div style={{ height: '42px', background: '#fef2f2', border: '2px solid #fecaca', borderRadius: '10px', padding: '0 16px', display: 'flex', alignItems: 'center' }}>
          <span style={{ fontWeight: '800', color: '#dc2626', fontSize: '15px' }}>{form.customerPrevBalance.toFixed(decimalPlaces)}</span>
        </div>
      </div>
    )}
  </div>
)}

{/* ✅ الملاحظات لغير العميل (مورّد أو بدون طرف) - تظهر بشكل مستقل هنا */}
{!isCustomerParty && (
  <div style={{ marginTop: '16px' }}>
    <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#64748b', marginBottom: '6px' }}>{t('invoice.fields.notes')}</label>
    <input name="notes" value={form.notes} onChange={handleChange} readOnly={viewMode}
      placeholder="اكتب ملاحظاتك هنا..."
      style={{ width: '100%', height: '42px', border: '2px solid #e2e8f0', borderRadius: '10px', padding: '0 14px', fontSize: '14px', fontWeight: '600', background: '#f8fafc', outline: 'none', boxSizing: 'border-box', color: '#1e293b' }} />
  </div>
)}

          

          {/* Reference */}
          {isInbound && (
            <div style={{ marginTop: '16px', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '12px' : '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#64748b', marginBottom: '6px' }}>{t('invoice.fields.referenceNumber')}</label>
                <input name="referenceNumber" value={form.referenceNumber} onChange={handleChange} readOnly={viewMode}
                  style={{ width: '100%', height: '42px', border: '2px solid #e2e8f0', borderRadius: '10px', padding: '0 14px', fontSize: '14px', fontWeight: '700', background: '#f8fafc', outline: 'none', boxSizing: 'border-box', color: '#1e293b' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#64748b', marginBottom: '6px' }}>{t('invoice.fields.referenceDate')}</label>
                <input name="referenceDate" type="date" value={form.referenceDate} onChange={handleChange} readOnly={viewMode}
                  style={{ width: '100%', height: '42px', border: '2px solid #e2e8f0', borderRadius: '10px', padding: '0 14px', fontSize: '14px', fontWeight: '700', background: '#f8fafc', outline: 'none', boxSizing: 'border-box', color: '#1e293b' }} />
              </div>
            </div>
          )}
        </div>

        {/* ─ Section 2: Items Toolbar ─ */}
        {!viewMode && (
          <div style={{ background: 'white', borderRadius: '16px', padding: isMobile ? '12px' : '14px 20px', marginBottom: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px' }}>
            <button type="button" onClick={handleAddLine}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '10px', padding: '0 18px', height: '42px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap', width: isMobile ? '100%' : 'auto' }}>
              + {t('invoice.buttons.addItemLine')}
            </button>
            <button type="button" onClick={() => setBulkOpen(true)}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', background: '#eef2ff', color: '#4f46e5', border: '2px solid #c7d2fe', borderRadius: '10px', padding: '0 16px', height: '42px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap', width: isMobile ? '100%' : 'auto' }}>
              + {t('invoice.buttons.insertmulti')}
            </button>
            <button type="button" onClick={() => setImportOpen(true)}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', background: '#ecfdf5', color: '#059669', border: '2px solid #a7f3d0', borderRadius: '10px', padding: '0 16px', height: '42px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap', width: isMobile ? '100%' : 'auto' }}>
              + {t('invoice.buttons.ImportInv')}
            </button>
            <input
              placeholder={t('scanBarcode') || '🔍 scan barcode...'}
              value={barcode} onChange={(e) => setBarcode(e.target.value)}
              onKeyDown={handleBarcodeKeyDown}
              onBlur={(e) => { if (!barcodeLoading && e.target.value.trim()) insertProductByBarcode(e.target.value); }}
              disabled={barcodeLoading} autoFocus
              style={{ flex: 1, minWidth: isMobile ? '100%' : '200px', width: isMobile ? '100%' : 'auto', height: '42px', border: '2px solid #bfdbfe', borderRadius: '10px', padding: '0 16px', fontSize: '14px', fontWeight: '600', background: '#eff6ff', outline: 'none', color: '#1e40af' }} />
          </div>
        )}

        {/* ─ Section 3: Items Table ─ */}
        <div style={{ background: 'white', borderRadius: '16px', marginBottom: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table style={{ width: '100%', minWidth: isMobile ? '980px' : '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ background: '#1e293b', color: 'white' }}>
                  <th style={{ padding: '14px 12px', textAlign: 'center', fontWeight: '700', fontSize: '13px', whiteSpace: 'nowrap', width: '40px' }}>#</th>
                  {visibleItemFields.ProductId   && <th style={{ padding: '14px 12px', textAlign: 'center', fontWeight: '700', fontSize: '13px', minWidth: '180px' }}>{t('invoice.fields.item')}</th>}
                  {visibleItemFields.Description && <th style={{ padding: '14px 12px', textAlign: 'center', fontWeight: '700', fontSize: '13px', minWidth: '140px' }}>{t('invoice.fields.description')}</th>}
                  {visibleItemFields.Unit        && <th style={{ padding: '14px 12px', textAlign: 'center', fontWeight: '700', fontSize: '13px', minWidth: '110px' }}>{t('invoice.fields.unit')}</th>}
                  {visibleItemFields.Quantity    && <th style={{ padding: '14px 12px', textAlign: 'center', fontWeight: '700', fontSize: '13px', minWidth: '100px' }}>{t('invoice.fields.quantity')}</th>}
                  {visibleItemFields.UnitPrice   && <th style={{ padding: '14px 12px', textAlign: 'center', fontWeight: '700', fontSize: '13px', minWidth: '110px' }}>{t('invoice.fields.price')}</th>}
                  {visibleItemFields.LineTotal   && <th style={{ padding: '14px 12px', textAlign: 'center', fontWeight: '700', fontSize: '13px', minWidth: '100px' }}>{t('invoice.fields.total')}</th>}
                  {visibleItemFields.Discount    && <th style={{ padding: '14px 12px', textAlign: 'center', fontWeight: '700', fontSize: '13px', minWidth: '100px' }}>{t('invoice.fields.discount')}</th>}
                  {visibleItemFields.TaxPercent  && <th style={{ padding: '14px 12px', textAlign: 'center', fontWeight: '700', fontSize: '13px', minWidth: '90px' }}>{t('invoice.fields.taxPercent')}</th>}
                  {visibleItemFields.Net         && <th style={{ padding: '14px 12px', textAlign: 'center', fontWeight: '700', fontSize: '13px', minWidth: '100px', background: '#1d4ed8' }}>{t('invoice.fields.net')}</th>}
                  {!viewMode                     && <th style={{ padding: '14px 8px', width: '44px' }}></th>}
                </tr>
              </thead>
              <tbody>
                {form.lines.map((ln, i) => {
                  const qty = parseFloat(ln.qty) || 0;
                  const price = parseFloat(ln.price) || 0;
                  const discount = parseFloat(ln.discount) || 0;
                  const taxRate = parseFloat(ln.taxRate) || 0;
                  const lineTotal = qty * price;
                  const afterDisc = lineTotal - discount;
                  const vat = priceIncludesVAT ? afterDisc - afterDisc / (1 + taxRate) : afterDisc * taxRate;
                  const lineNet = priceIncludesVAT ? afterDisc : afterDisc + vat;
                  const rowBg = i % 2 === 0 ? '#ffffff' : '#f8fafc';

                  return (
                    <tr key={i} style={{ background: rowBg, borderBottom: '1px solid #f1f5f9' }}
                      onMouseOver={e => e.currentTarget.style.background = '#eff6ff'}
                      onMouseOut={e => e.currentTarget.style.background = rowBg}>

                      <td style={{ padding: '10px 12px', textAlign: 'center', color: '#94a3b8', fontWeight: '700', fontSize: '13px' }}>{i + 1}</td>

                      {visibleItemFields.ProductId && (
                        <td style={{ padding: '8px' }}>
                          <input type="text" list={`product-options-${i}`}
                            data-line-field={`productName-${i}`}
                            value={ln.productName} disabled={viewMode}
                            onChange={(e) => { handleLineChange(i, "productName", e.target.value); setProductSearch(e.target.value); }}
                            placeholder={t('invoice.placeholders.searchItem')}
                            style={{ width: '100%', height: '38px', border: '2px solid #e2e8f0', borderRadius: '8px', padding: '0 10px', fontSize: '14px', fontWeight: '700', background: 'white', outline: 'none', boxSizing: 'border-box' }} />
                          <datalist id={`product-options-${i}`}>
                            {products.filter(p => (p.arabicName || "").toLowerCase().includes((productSearch || "").toLowerCase()))
                              .map(p => <option key={p.id} value={p.arabicName} />)}
                          </datalist>
                        </td>
                      )}

                      {visibleItemFields.Description && (
                        <td style={{ padding: '8px' }}>
                          <input type="text" data-line-field={`description-${i}`} value={ln.description ?? ''} disabled={viewMode}
                            onChange={(e) => handleLineChange(i, "description", e.target.value)}
                            placeholder={t('invoice.placeholders.description')}
                            style={{ width: '100%', height: '38px', border: '2px solid #e2e8f0', borderRadius: '8px', padding: '0 10px', fontSize: '14px', fontWeight: '600', background: 'white', outline: 'none', boxSizing: 'border-box' }} />
                        </td>
                      )}

                      {visibleItemFields.Unit && (
                        <td style={{ padding: '8px' }}>
                          <select data-line-field={`unitId-${i}`} value={ln.unitId} disabled={viewMode}
                            onChange={(e) => handleLineChange(i, "unitId", e.target.value)}
                            style={{ width: '100%', height: '38px', border: '2px solid #e2e8f0', borderRadius: '8px', padding: '0 8px', fontSize: '14px', fontWeight: '700', background: 'white', outline: 'none', boxSizing: 'border-box' }}>
                            <option value={0}>—</option>
                            {ln.unitsList.map(u => <option key={u.unitId} value={u.unitId}>{u.unitNameAr}</option>)}
                          </select>
                        </td>
                      )}

                      {visibleItemFields.Quantity && (
                        <td style={{ padding: '8px' }}>
                          <input type="text" inputMode="decimal" data-line-field={`qty-${i}`} value={ln.qty} disabled={viewMode}
                            onChange={(e) => { const v = e.target.value; if (v === '' || /^[0-9]*\.?[0-9]*$/.test(v)) handleLineChange(i, "qty", v); }}
                            onBlur={(e) => handleLineChange(i, "qty", (parseFloat(e.target.value) || 0).toFixed(decimalPlaces))}
                            onKeyDown={(e) => handleLineFieldKeyDown(e, i, "qty")}
                            onFocus={(e) => e.target.select()}
                            style={{ width: '90px', height: '38px', border: '2px solid #e2e8f0', borderRadius: '8px', padding: '0 8px', fontSize: '14px', fontWeight: '700', background: 'white', outline: 'none', textAlign: 'center', boxSizing: 'border-box' }} />
                        </td>
                      )}

                      {visibleItemFields.UnitPrice && (
                        <td style={{ padding: '8px' }}>
                          <input type="text" inputMode="decimal" data-line-field={`price-${i}`} value={ln.price} disabled={viewMode}
                            onChange={(e) => { const v = e.target.value; if (v === '' || /^[0-9]*\.?[0-9]*$/.test(v)) handleLineChange(i, "price", v); }}
                            onBlur={(e) => handleLineChange(i, "price", (parseFloat(e.target.value) || 0).toFixed(decimalPlaces))}
                            onKeyDown={(e) => handleLineFieldKeyDown(e, i, "price")}
                            onFocus={(e) => e.target.select()}
                            style={{ width: '100px', height: '38px', border: '2px solid #e2e8f0', borderRadius: '8px', padding: '0 8px', fontSize: '14px', fontWeight: '700', background: 'white', outline: 'none', textAlign: 'center', boxSizing: 'border-box' }} />
                        </td>
                      )}

                      {visibleItemFields.LineTotal && (
                        <td style={{ padding: '10px 12px', textAlign: 'center', fontWeight: '700', color: '#374151', fontSize: '14px', background: '#f9fafb', whiteSpace: 'nowrap' }}>
                          {lineTotal.toFixed(decimalPlaces)}
                        </td>
                      )}

                      {visibleItemFields.Discount && (
                        <td style={{ padding: '8px' }}>
                          <input type="text" inputMode="decimal" data-line-field={`discount-${i}`} value={ln.discount} disabled={viewMode}
                            onChange={(e) => { const v = e.target.value; if (v === '' || /^[0-9]*\.?[0-9]*$/.test(v)) handleLineChange(i, "discount", v); }}
                            onBlur={(e) => handleLineChange(i, "discount", (parseFloat(e.target.value) || 0).toFixed(decimalPlaces))}
                            onKeyDown={(e) => handleLineFieldKeyDown(e, i, "discount")}
                            onFocus={(e) => e.target.select()}
                            style={{ width: '90px', height: '38px', border: '2px solid #e2e8f0', borderRadius: '8px', padding: '0 8px', fontSize: '14px', fontWeight: '700', background: 'white', outline: 'none', textAlign: 'center', boxSizing: 'border-box' }} />
                        </td>
                      )}

                      {visibleItemFields.TaxPercent && (
                        <td style={{ padding: '8px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}>
                            <input type="number" step={stepValue} readOnly={viewMode}
                              value={(parseFloat(ln.taxRate) * 100).toFixed(decimalPlaces)}
                              onChange={(e) => handleLineChange(i, "taxRate", parseFloat(e.target.value) / 100)}
                              style={{ width: '60px', height: '38px', border: '2px solid #e2e8f0', borderRadius: '8px', padding: '0 6px', fontSize: '14px', fontWeight: '700', background: 'white', outline: 'none', textAlign: 'center', boxSizing: 'border-box' }} />
                            <span style={{ color: '#94a3b8', fontSize: '12px', fontWeight: '700' }}>%</span>
                          </div>
                        </td>
                      )}

                      {visibleItemFields.Net && (
                        <td style={{ padding: '10px 12px', textAlign: 'center', fontWeight: '800', color: '#1d4ed8', fontSize: '15px', background: '#eff6ff', whiteSpace: 'nowrap' }}>
                          {lineNet.toFixed(decimalPlaces)}
                        </td>
                      )}

                      {!viewMode && (
                        <td style={{ padding: '8px', textAlign: 'center' }}>
                          <button type="button" onClick={() => handleRemoveLine(i)}
                            style={{ width: '32px', height: '32px', background: '#fef2f2', border: 'none', borderRadius: '8px', color: '#ef4444', fontSize: '14px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto', opacity: 0.7 }}
                            onMouseOver={e => { e.currentTarget.style.opacity = 1; e.currentTarget.style.background = '#fee2e2'; }}
                            onMouseOut={e => { e.currentTarget.style.opacity = 0.7; e.currentTarget.style.background = '#fef2f2'; }}>
                            ✕
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}

                {form.lines.length === 0 && (
                  <tr>
                    <td colSpan={
                      1 + (visibleItemFields.ProductId ? 1 : 0) + (visibleItemFields.Description ? 1 : 0)
                      + (visibleItemFields.Unit ? 1 : 0) + (visibleItemFields.Quantity ? 1 : 0)
                      + (visibleItemFields.UnitPrice ? 1 : 0) + (visibleItemFields.LineTotal ? 1 : 0)
                      + (visibleItemFields.Discount ? 1 : 0) + (visibleItemFields.TaxPercent ? 1 : 0)
                      + (visibleItemFields.Net ? 1 : 0) + (!viewMode ? 1 : 0)
                    } style={{ padding: '60px 20px', textAlign: 'center' }}>
                      <div style={{ color: '#cbd5e1', fontSize: '48px', marginBottom: '12px' }}>📋</div>
                      <div style={{ color: '#94a3b8', fontWeight: '700', fontSize: '15px', marginBottom: '12px' }}>{t('invoice.messages.noLinesYet')}</div>
                      {!viewMode && (
                        <button type="button" onClick={handleAddLine}
                          style={{ color: '#2563eb', background: 'none', border: 'none', fontWeight: '700', fontSize: '14px', cursor: 'pointer', textDecoration: 'underline' }}>
                          + {t('invoice.buttons.addItemLine')}
                        </button>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ─ Section 4: Footer ─ */}
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-start' }}>

          {/* Action Buttons */}
          <div style={{ flex: 1, width: isMobile ? '100%' : 'auto', background: 'white', borderRadius: '16px', padding: isMobile ? '12px' : '16px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0', display: 'flex', flexWrap: 'wrap', gap: '10px', minWidth: isMobile ? '100%' : '300px' }}>
            {!viewMode && (
              <button type="button" onClick={resetForm}
                disabled={isSaving}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '10px', padding: '0 16px', height: '42px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', width: isMobile ? '100%' : 'auto' }}>
                🧾 {t('invoice.buttons.new')}
              </button>
            )}
            {!viewMode && !isApproved && (
              <button type="submit" disabled={isSaving}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', background: '#059669', color: 'white', border: 'none', borderRadius: '10px', padding: '0 20px', height: '42px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', width: isMobile ? '100%' : 'auto' }}>
                💾 {t('invoice.buttons.saveInvoice')}
              </button>
            )}
            {id && (
              <button type="button" onClick={() => window.open(`/invoices/print/${id}`, "_blank")}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', background: '#334155', color: 'white', border: 'none', borderRadius: '10px', padding: '0 16px', height: '42px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', width: isMobile ? '100%' : 'auto' }}>
                🖨️ طباعة
              </button>
            )}
            <button type="button" onClick={handleExportExcel}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', background: '#ecfdf5', color: '#059669', border: '2px solid #a7f3d0', borderRadius: '10px', padding: '0 16px', height: '42px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', width: isMobile ? '100%' : 'auto' }}>
              📊 Excel
            </button>
            {form.journalEntryGlobalId && (
              <button type="button" onClick={() => window.open(`/journal-entries/${form.journalEntryGlobalId}/view`, "_blank")}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', background: '#faf5ff', color: '#7c3aed', border: '2px solid #ddd6fe', borderRadius: '10px', padding: '0 16px', height: '42px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', width: isMobile ? '100%' : 'auto' }}>
                👁️ القيد
              </button>
            )}
            {editingId && showApproveButton && !isApproved && hasPermission(getApprovePermission(form.invoiceTypeId)) && (
              <button type="button" onClick={handleApprove} disabled={isSaving}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '10px', padding: '0 20px', height: '42px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', width: isMobile ? '100%' : 'auto' }}>
                ✅ {t('invoice.buttons.approveInvoice')}
              </button>
            )}
            {editingId && isApproved && canUnapprove && (
              <button type="button" onClick={handleUnapprove} disabled={isSaving}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', background: '#f59e0b', color: 'white', border: 'none', borderRadius: '10px', padding: '0 20px', height: '42px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', width: isMobile ? '100%' : 'auto' }}>
                ↩️ إلغاء الاعتماد
              </button>
            )}
            <button type="button" onClick={handleCustomizeLayout}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', background: '#f8fafc', color: '#64748b', border: '2px solid #e2e8f0', borderRadius: '10px', padding: '0 16px', height: '42px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', width: isMobile ? '100%' : 'auto' }}>
              ⚙️ {t('invoice.buttons.customizeAppearance')}
            </button>
          </div>

          {/* Totals Panel */}
          <div style={{ width: isMobile ? '100%' : '300px', background: 'white', borderRadius: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0', overflow: 'hidden', flexShrink: 0 }}>
            {[
              { label: t('invoice.summary.subtotal'), value: totals.subtotal.toFixed(decimalPlaces), isInput: false },
              { label: t('invoice.summary.invoiceDiscount'), value: null, isInput: true },
              { label: t('invoice.summary.afterDiscount'), value: totals.totalAfterDiscount.toFixed(decimalPlaces), isInput: false },
              { label: t('invoice.summary.vat'), value: totals.totalVAT.toFixed(decimalPlaces), isInput: false },
            ].map(({ label, value, isInput }) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '8px' : 0, padding: '12px 18px', borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ fontSize: '14px', fontWeight: '700', color: '#64748b' }}>{label}</span>
                {isInput ? (
                  <input type="number" step={stepValue} name="discount" value={form.discount}
                    onChange={handleChange} readOnly={viewMode}
                    style={{ width: isMobile ? '100%' : '110px', height: '36px', border: '2px solid #e2e8f0', borderRadius: '8px', padding: '0 10px', fontSize: '14px', fontWeight: '700', textAlign: 'center', background: '#f8fafc', outline: 'none' }} />
                ) : (
                  <span style={{ fontSize: '15px', fontWeight: '800', color: '#1e293b' }}>{value}</span>
                )}
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 20px', background: 'linear-gradient(to left, #1d4ed8, #2563eb)' }}>
              <span style={{ color: 'white', fontSize: '16px', fontWeight: '700' }}>{t('invoice.summary.net')}</span>
              <span style={{ color: 'white', fontSize: isMobile ? '22px' : '28px', fontWeight: '900' }}>{totals.net.toFixed(decimalPlaces)}</span>
            </div>
          </div>
        </div>

      </form>
    </div>

    {/* ── BULK INSERT ── */}
    {bulkOpen && !viewMode && (
      <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)' }} onClick={() => setBulkOpen(false)} />
        <div style={{ position: 'relative', background: 'white', width: '100%', maxWidth: '640px', borderRadius: '20px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 22px', borderBottom: '1px solid #f1f5f9', background: '#f8fafc' }}>
            <h3 style={{ margin: 0, fontWeight: '800', fontSize: '16px', color: '#1e293b' }}>📦 إدراج متعدد للأصناف</h3>
            <button onClick={() => setBulkOpen(false)} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#94a3b8', fontWeight: '700' }}>✕</button>
          </div>
          <div style={{ padding: '16px' }}>
            <input placeholder="بحث عن صنف..." value={bulkSearch} onChange={(e) => setBulkSearch(e.target.value)}
              style={{ width: '100%', height: '42px', border: '2px solid #e2e8f0', borderRadius: '10px', padding: '0 14px', fontSize: '14px', fontWeight: '600', background: '#f8fafc', outline: 'none', marginBottom: '12px', boxSizing: 'border-box' }} />
            <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '10px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead style={{ background: '#f8fafc', position: 'sticky', top: 0 }}>
                  <tr>
                    <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: '700', fontSize: '12px', color: '#64748b', width: '44px' }}>✓</th>
                    <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: '700', fontSize: '12px', color: '#64748b' }}>الصنف</th>
                    <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: '700', fontSize: '12px', color: '#64748b', width: '90px' }}>الوحدة</th>
                    <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: '700', fontSize: '12px', color: '#64748b', width: '90px' }}>الكمية</th>
                  </tr>
                </thead>
                <tbody>
                  {products.filter(p => (p.arabicName || "").toLowerCase().includes((bulkSearch || "").toLowerCase())).map(p => {
                    const unitsList = Array.isArray(p.units) ? p.units : [];
                    const bu = unitsList.find(u => u.isBaseUnit) || {};
                    const qtyVal = bulkSelected[p.id] ?? 0;
                    const checked = Object.prototype.hasOwnProperty.call(bulkSelected, p.id);
                    return (
                      <tr key={p.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                          <Checkbox checked={!!checked} onChange={(e) => toggleBulkProduct(p.id, e.target.checked)} />
                        </td>
                        <td style={{ padding: '8px 12px', fontWeight: '700', color: '#1e293b' }}>{p.arabicName}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'center', color: '#64748b', fontSize: '13px' }}>{bu?.unitNameAr || '—'}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                          <input type="number" min="0" step="1" value={qtyVal || 1}
                            onChange={(e) => setBulkProductQty(p.id, e.target.value)}
                            style={{ width: '70px', height: '34px', border: '2px solid #e2e8f0', borderRadius: '8px', padding: '0 8px', fontSize: '14px', fontWeight: '700', textAlign: 'center', background: 'white', outline: 'none' }} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '14px 22px', borderTop: '1px solid #f1f5f9', background: '#f8fafc' }}>
            <button onClick={() => setBulkOpen(false)}
              style={{ padding: '0 18px', height: '40px', background: 'white', border: '2px solid #e2e8f0', borderRadius: '10px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', color: '#64748b' }}>إلغاء</button>
            <button onClick={handleBulkInsert}
              style={{ padding: '0 20px', height: '40px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '700', cursor: 'pointer' }}>إدراج الأصناف المحددة</button>
          </div>
        </div>
      </div>
    )}

    <Dialog open={openCustomerModal} onClose={() => setOpenCustomerModal(false)} dir="rtl" maxWidth="sm" fullWidth>
      <DialogTitle>إضافة عميل جديد</DialogTitle>
      <DialogContent>
        <CustomerForm isModal={true} onSuccess={handleCustomerSuccess} onCancel={() => setOpenCustomerModal(false)} />
      </DialogContent>
    </Dialog>

    <ImportInvoiceItemsDialog open={importOpen} onClose={() => setImportOpen(false)} onImport={handleImportSuccess} products={products} />

  </div>
);
}
